/**
 * PageHeader component — Karr Design System.
 *
 * Serif title with optional breadcrumbs and action buttons.
 * Follows the "published document" feel per ui_ux_design.md.
 *
 * ## Universal Header System (v2.2)
 *
 * Auto-collapses when user scrolls down to maximize data real estate.
 * Full header shows on initial load; collapses to a compact single line
 * after scrolling past a threshold (60px).
 *
 * The AppLayout now auto-renders a PageHeader from the route registry.
 * If a page ALSO renders <PageHeader>, it will:
 *   - SKIP the title/subtitle/breadcrumbs (already shown by layout)
 *   - ONLY render the actions slot (buttons, filters, etc.)
 *
 * This means:
 *   - New pages get a header automatically (just add to routeRegistry.ts)
 *   - Existing pages with <PageHeader actions={...}> keep their action buttons
 *   - No duplicate headers
 *
 * @since 2.2.0 — Added auto-collapse on scroll
 */
import { useContext, useState, useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { ChevronRight, HelpCircle } from 'lucide-react';
import { useRouterState } from '@tanstack/react-router';
import { LayoutHeaderContext } from '../contexts/LayoutHeaderContext';
import { getModuleKeyForPath } from '../guide/guidePaths';
import { HEADER_ACTIONS_ID, HEADER_FIGURES_ID, HEADER_ROOT_ID, HEADER_TABS_ID, useHeaderHasFigures, useHeaderHost } from './headerHost';

/**
 * "Explain this" pill that opens the Karr Coach for the current module.
 *
 * Until first opened it carries a ring, and nothing else: it pulsed for ever
 * until 11 Oct 2026 (headstats), a second endless animation in the one row
 * that cannot afford any (docs/HEADER_FIGURES_SPEC.md, principle 2).
 */
function CoachTrigger({ compact }: { compact?: boolean }) {
    const routerState = useRouterState();
    const moduleKey = getModuleKeyForPath(routerState.location.pathname);
    const [seen, setSeen] = useState<boolean>(() => {
        if (!moduleKey) return true;
        try { return localStorage.getItem(`karr_coach_seen_${moduleKey}`) === '1'; } catch { return true; }
    });

    if (!moduleKey) return null;

    const open = () => {
        setSeen(true);
        window.dispatchEvent(new CustomEvent('karr-coach:open'));
    };

    if (compact) {
        return (
            <button onClick={open} aria-label="Explain this page" title="Explain this page"
                className={`shrink-0 rounded-full p-1 text-teal-600 hover:bg-teal-50 dark:hover:bg-teal-950/40 ${!seen ? 'ring-2 ring-teal-300' : ''}`}>
                <HelpCircle size={16} />
            </button>
        );
    }
    return (
        <button onClick={open}
            className={`group ml-1 inline-flex shrink-0 items-center gap-1 rounded-full border border-teal-200 bg-teal-50 px-2.5 py-1 text-[11px] font-semibold text-teal-700 transition-colors hover:bg-teal-100 dark:border-teal-900 dark:bg-teal-950/40 dark:text-teal-300 ${!seen ? 'ring-2 ring-teal-300 ring-offset-1' : ''}`}>
            <HelpCircle size={13} />
            Explain this
        </button>
    );
}

/**
 * The action row, in ONE place because all three headers below render it and
 * all three were wrong in the same way.
 *
 * It was `flex items-center gap-2 flex-shrink-0`: no wrapping and a refusal to
 * shrink, inside a `justify-between` parent. A row of four or five labelled
 * buttons is around 700px wide, so on a phone it could not fit and could not
 * give — and because the parent packs from both ends, it was pushed off the
 * LEFT. Measured on 29 Aug 2026 at 390px: /expenses started at -108px and
 * /reports at -359px, taking Compare, Expand all, Download and Scan Receipt
 * somewhere no finger could reach and no scrollbar could follow.
 *
 * `flex-wrap` is the fix the checklist already prescribes (rule 1: rank them or
 * let the row wrap, never shorten the label to buy a release). `justify-end`
 * keeps the buttons against the right edge on a wide screen, which is where
 * they have always been, and `min-w-0` lets the row take the width it is given
 * rather than the width it wants.
 */
const ACTIONS_ROW = 'flex flex-wrap items-center justify-end gap-2 min-w-0';

/**
 * The id of the box a page may put its own controls INSIDE the header row.
 *
 * ## Why a slot and not a second bar (deskrow, 10 Sep 2026)
 *
 * The audit desk needed the year and the four stages on screen the whole time
 * you work, so it grew a second sticky bar under this one. Founder, 10 Sep
 * 2026: *"i have been telling u to bring everything here in the row of This
 * business's audit why create another row beanth — that way consultants will
 * have least realestate to work"*. Two pinned bars is two ribbons' worth of the
 * fold gone before a consultant reads a line of their own work.
 *
 * So a page can hand its persistent controls to THIS row, through
 * {@link HeaderSlot}. There is exactly one host, it lives in the sticky header,
 * and whatever goes in it must be a single line high: this row is the last
 * place in Karr that may grow.
 */
export const HEADER_SLOT_ID = 'karr-page-header-slot';

/**
 * Where {@link HeaderSlot}'s children land. Rendered by the header itself.
 *
 * ONE element in both the tall and the compact shape (shrinkhead, 2 Oct 2026):
 * only its classes change, so what a page put here is not remounted by a
 * collapse.
 */
function HeaderSlotHost({ expanded }: { expanded?: boolean }) {
    return (
        <div
            id={HEADER_SLOT_ID}
            /* `basis-full` until there is real room: the title, a year picker
               and four stage pills cannot share 358px on a phone, so squeezing
               them put the pills where no finger and no scrollbar could reach.
               Its own line inside the SAME bar keeps one sticky row and gives
               the slot a width it can scroll. `lg:basis-0` (not `basis-auto`)
               is what puts it back on the title's line: flex wraps on the base
               size, so a slot whose base is its full content — 600px of pills —
               wraps at 768 however much its contents could have shrunk.
               Growing from zero does not. */
            className={`order-2 flex min-w-0 items-center gap-2 empty:hidden basis-full lg:basis-0 lg:grow ${
                expanded ? 'mt-2 lg:mt-0 lg:mx-3' : 'lg:justify-end lg:ml-3'
            }`}
        />
    );
}

export interface Breadcrumb {
    label: string;
    href?: string;
}

export interface PageHeaderProps {
    /** Page title (rendered in Serif font) */
    title: string;
    /** Optional subtitle or description */
    subtitle?: string;
    /** Breadcrumb trail */
    breadcrumbs?: Breadcrumb[];
    /** Action elements (buttons) rendered on the right */
    actions?: ReactNode;
    /** Callback when a breadcrumb is clicked */
    onBreadcrumbClick?: (href: string) => void;
    /** If true, this is the layout-level header (internal use) */
    _isLayoutHeader?: boolean;
    /**
     * Start compact and stay compact — the one-line bar at every scroll
     * position, ~32px instead of ~80px.
     *
     * For a screen whose own content orients the reader on arrival. The audit
     * desk is the case that asked for it (deskcards, 10 Sep 2026): its head
     * card already names the client, the year, the audit and the firm, so a
     * 28px serif title and a subtitle above it were spending the top of the
     * screen restating what the page says better one line lower. Founder, 10
     * Sep 2026: *"let the ribbon on the top get small very small just to show
     * what this is and give major real estate to the main things"*.
     *
     * Set it in `routeRegistry.ts` (`dense: true`) rather than per page, so
     * how tall a screen's header is stays a property of the route.
     */
    dense?: boolean;
}


/**
 * Page header with Serif title, breadcrumbs, and action buttons.
 *
 * When rendered INSIDE AppLayout (after the layout's own header), only
 * the `actions` slot is rendered — title and breadcrumbs are handled
 * by the universal header from the route registry.
 *
 * Auto-collapses to a compact single-line bar when the user scrolls
 * past 60px, maximizing viewport space for data tables and content.
 * A `dense` route is drawn in that compact bar from the start.
 */
export function PageHeader({
    title,
    subtitle,
    breadcrumbs,
    actions,
    onBreadcrumbClick,
    _isLayoutHeader,
    dense,
}: PageHeaderProps) {
    const layoutAlreadyRenderedHeader = useContext(LayoutHeaderContext);
    const [scrolled, setScrolled] = useState(false);
    const headerRef = useRef<HTMLDivElement>(null);
    /** The one-liner: because the route asked for it, or because we scrolled. */
    const collapsed = dense || scrolled;
    /** The trail WITHOUT the current page — the compact bar's title is that. */
    const trail = (breadcrumbs ?? []).slice(0, -1);
    /** The bar's actions box, which the page's own instance portals into. */
    const actionsHost = useHeaderHost(HEADER_ACTIONS_ID, layoutAlreadyRenderedHeader && !_isLayoutHeader && !!actions);
    /** A page put its figures in the bar: they replace the subtitle (headstats). */
    const hasFigures = useHeaderHasFigures();

    /*
     * Collapse on scroll. Rewritten 2 Oct 2026 (reportsplus) after the founder
     * scrolled the Day Book on /reports and the header never shrank: *"the
     * header is still there what have u closed"*. Three things were wrong:
     *
     * 1. The scroller was picked by "which ancestor is scrollable NOW", once,
     *    at mount. Reports mounts on a spinner, so nothing scrolled yet, the
     *    walk went past <main>, and the header listened to an element that
     *    never scrolls. It now picks the ancestor whose CSS says it scrolls.
     * 2. It refused to collapse unless the page could scroll 200px more than
     *    the height collapsing released, to dodge a flicker (see below). A
     *    page whose work sits in its own scroll box (the Day Book sheet) is
     *    shorter than that, so it never collapsed.
     * 3. Scrolling INSIDE a table was invisible to it. It listens in the
     *    capture phase now, so the sheet's own scroll counts too.
     *
     * The flicker it was dodging: collapsing releases the header's height,
     * the page gets shorter, the browser clamps scrollTop back under the
     * threshold, the header expands, and round it goes. Instead of refusing
     * to collapse, the released height is given back as padding at the very
     * bottom of the scroller while collapsed, so the page never gets shorter
     * and nothing clamps. Anchoring stays off, and the hysteresis stays.
     */
    useEffect(() => {
        // A dense route is already in the compact bar and has nowhere to
        // collapse to, so it neither listens nor re-renders on scroll.
        if (dense) return;
        const el = headerRef.current;
        if (!el) return;

        let found: HTMLElement | null = el.parentElement;
        while (found) {
            const oy = getComputedStyle(found).overflowY;
            if (oy === 'auto' || oy === 'scroll') break;
            found = found.parentElement;
        }
        if (!found) return;
        const p: HTMLElement = found;

        const COLLAPSE_AT = 60;
        const EXPAND_AT = 12;

        p.style.overflowAnchor = 'none';
        let collapsed = false;
        /** The scroll box inside the page that last moved (a sheet, a table). */
        let inner: HTMLElement | null = null;
        let pad = 0;
        const setPad = (px: number) => { pad = px; p.style.paddingBottom = px > 0 ? `${px}px` : ''; };

        let ticking = false;
        const decide = () => {
            ticking = false;
            if (inner && !inner.isConnected) inner = null;
            const y = Math.max(p.scrollTop, inner ? inner.scrollTop : 0);
            const next = collapsed ? y > EXPAND_AT : y > COLLAPSE_AT;
            if (next === collapsed) return;
            collapsed = next;
            if (next) {
                // Over-pad first (the whole tall height), so nothing can clamp
                // in the frame before the collapse renders; then trim to the
                // LEAST that keeps the page scrollable past EXPAND_AT. Not the
                // whole released height: that showed as a blank band under the
                // last row once you reached the end (founder, 2 Oct 2026,
                // circling it on the Day Book sheet: "please use that space").
                // A page with room to scroll gets no pad at all; only one too
                // short to stay past EXPAND_AT gets the few pixels it needs.
                const tall = el.getBoundingClientRect().height;
                setPad(tall);
                setScrolled(true);
                requestAnimationFrame(() => {
                    const range = p.scrollHeight - pad - p.clientHeight;
                    setPad(Math.max(0, EXPAND_AT + 4 - range));
                });
            } else {
                setPad(0);
                setScrolled(false);
            }
        };
        const onScroll = (e: Event) => {
            const t = e.target;
            if (t instanceof HTMLElement && t !== p && !el.contains(t) && t.scrollHeight > t.clientHeight + 1) inner = t;
            if (!ticking) { ticking = true; requestAnimationFrame(decide); }
        };

        p.addEventListener('scroll', onScroll, { passive: true, capture: true });
        decide();

        return () => {
            p.removeEventListener('scroll', onScroll, { capture: true });
            p.style.overflowAnchor = '';
            setPad(0);
        };
    }, [dense]);

    /**
     * Publish the header's height, so a sticky table head can sit under it.
     *
     * MEASURED, not assumed. It used to be the literal pair 32px / 80px, which
     * was true until a page put controls in the row (`HEADER_SLOT_ID`,
     * deskrow) and the compact bar grew to fit them. A hard-coded height that
     * is now wrong slides every sticky thead in the app under the ribbon by the
     * difference, silently.
     */
    useEffect(() => {
        const el = headerRef.current;
        const root = document.documentElement;
        /**
         * railstick, 11 Sep 2026 - ONLY the instance that actually draws the bar
         * may publish its height.
         *
         * Two `PageHeader`s run on most screens: the layout's, which renders the
         * bar, and the page's own, which exists to hand over its `actions` and
         * returns before the bar. That second one's ref is therefore null, and
         * it used to publish the hard-coded `80px` anyway. Effects run child
         * before parent, so the page's GUESS landed last and overwrote the
         * layout's real measurement.
         *
         * Measured on /tax-audit, /audit and /sales at 1440: the bar is 103.4px
         * and the variable said 80px. Every sticky `DataTable` head in Karr has
         * been sitting 23px UNDER the ribbon, its column names hidden behind it,
         * on every screen that has one.
         *
         * A route with no header publishes nothing now, and a consumer falls
         * back to the default in its own `var(--page-header-height, 80px)`,
         * which is what that default is for.
         */
        if (!el) return;
        const publish = () => {
            root.style.setProperty('--page-header-height', `${Math.round(el.getBoundingClientRect().height)}px`);
        };
        publish();
        const ro = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(publish);
        ro?.observe(el);
        return () => {
            ro?.disconnect();
            root.style.removeProperty('--page-header-height');
        };
    }, [collapsed]);

    // If the LAYOUT already rendered the header and this is NOT the layout's
    // own <PageHeader>, skip the chrome — only render actions if any.
    if (layoutAlreadyRenderedHeader && !_isLayoutHeader) {
        if (!actions) return null;
        // shrinkhead: into the bar, so they stay in reach while you scroll.
        // Until the host is found (one commit) nothing is drawn, rather than
        // drawing the row twice.
        return actionsHost ? createPortal(actions, actionsHost) : null;
    }

    /*
     * ONE tree in both shapes (shrinkhead, 2 Oct 2026). It used to be two, a
     * compact row and a tall block, swapped on the collapse, so anything a page
     * had put inside was unmounted and mounted again on every flick of the
     * wheel. Now the hosts stay put and only their classes and `order` move:
     *
     *   tall     title ................... figures | actions
     *            tabs (own line, full width)
     *   compact  title · pinned figures · slot · tabs (scroll) · actions (icons)
     *
     * Two things the old bar had are gone on purpose, because page actions
     * now live in it and both broke menus:
     *   - `overflow-hidden` clipped every dropdown under a button to the
     *     bar's own height.
     *   - `backdrop-blur` made the bar the containing block of every
     *     `position: fixed` child, so a menu's click-away backdrop covered the
     *     bar instead of the screen (header-backdrop-filter-traps-fixed-
     *     children). The bar is opaque instead: at 97% the rows scrolling
     *     under it still showed through as ghost text (founder, 2 Oct 2026).
     * And z-20, not z-[5]: a sticky `DataTable` head is z-10, and a menu
     * hanging off a header button must open OVER it, not under it.
     */
    return (
        <div
            ref={headerRef}
            id={_isLayoutHeader || !layoutAlreadyRenderedHeader ? HEADER_ROOT_ID : undefined}
            /* `data-compact` + `group/head`: what lives in the bar (tabs,
               buttons) reads the bar's shape from here, not from a prop. */
            data-compact={collapsed ? '' : undefined}
            className={`group/head sticky top-0 z-20 -mx-4 sm:-mx-6 px-4 sm:px-6 bg-white dark:bg-stone-900 border-b border-stone-200/80 dark:border-stone-800/80 ${
                collapsed && !dense ? 'shadow-[0_6px_16px_-12px_rgba(28,25,23,0.35)]' : ''
            }`}
            style={{
                transition: 'padding 200ms ease, box-shadow 200ms ease',
                paddingTop: collapsed ? '4px' : '12px',
                paddingBottom: collapsed ? '4px' : '12px',
                marginBottom: collapsed ? '8px' : '16px',
            }}
        >
            <div
                className="relative z-[1] flex flex-wrap items-center justify-between gap-x-3 gap-y-1"
                style={{ minHeight: collapsed ? '32px' : undefined }}
            >
                <div className={`order-1 min-w-0 ${collapsed ? 'lg:max-w-[18rem] lg:shrink-0' : ''}`}>
                    {/* Breadcrumbs, on their own line above the title. */}
                    {!collapsed && breadcrumbs && breadcrumbs.length > 0 && (
                        <nav className="flex items-center gap-1 mb-1 text-xs text-stone-400">
                            {breadcrumbs.map((crumb, i) => (
                                <span key={i} className="flex items-center gap-1">
                                    {crumb.href ? (
                                        <button
                                            onClick={() => onBreadcrumbClick?.(crumb.href!)}
                                            className="hover:text-stone-600 dark:hover:text-stone-300 transition-colors"
                                        >
                                            {crumb.label}
                                        </button>
                                    ) : (
                                        <span className="text-stone-500">{crumb.label}</span>
                                    )}
                                    {i < breadcrumbs.length - 1 && <ChevronRight size={12} />}
                                </span>
                            ))}
                        </nav>
                    )}

                    <div className="flex items-center gap-2 min-w-0">
                        {/* Compact: the trail inline, WITHOUT its last crumb. That
                            crumb is the current page, and the title right next to
                            it says the same words; on one line it came out as
                            "Home › This business's audit › This business's audit". */}
                        {collapsed && trail.length > 0 && (
                            <nav className="hidden sm:flex items-center gap-1 text-[11px] text-stone-400 shrink-0">
                                {trail.map((crumb, i) => (
                                    <span key={i} className="flex items-center gap-0.5">
                                        {crumb.href ? (
                                            <button
                                                onClick={() => onBreadcrumbClick?.(crumb.href!)}
                                                className="hover:text-stone-600 dark:hover:text-stone-300 transition-colors"
                                            >
                                                {crumb.label}
                                            </button>
                                        ) : (
                                            <span className="text-stone-500">{crumb.label}</span>
                                        )}
                                        <ChevronRight size={10} />
                                    </span>
                                ))}
                            </nav>
                        )}
                        <h1
                            className="text-stone-900 dark:text-stone-50 truncate"
                            style={{
                                fontSize: collapsed ? '0.95rem' : '1.75rem',
                                fontWeight: 700,
                                letterSpacing: collapsed ? '-0.015em' : '-0.025em',
                                lineHeight: collapsed ? 1.3 : 1.2,
                                transition: 'font-size 200ms ease',
                            }}
                        >
                            {title}
                        </h1>
                        <CoachTrigger compact={collapsed} />
                    </div>

                    {!collapsed && subtitle && !hasFigures && (
                        <p className="mt-1 text-[13px] text-stone-500 dark:text-stone-400 truncate">{subtitle}</p>
                    )}
                </div>

                {/* The page's figures (`HeaderFigures`, headstats 11 Oct
                    2026), where the hover drawing was. Tall, at `sm` and up:
                    on the title's line, growing from zero so they never wrap
                    the buttons off it, right-aligned against them and
                    scrolling inside themselves before they push anything.
                    Compact: the pinned one or two, `shrink-0`, after the
                    title. Under `sm`: a line of their own under the title,
                    40px for a thumb, and none at all once compact. */}
                <div
                    id={HEADER_FIGURES_ID}
                    className={`karr-header-figures min-w-0 empty:hidden ${
                        collapsed
                            ? 'order-1 hidden sm:flex shrink-0 items-center'
                            : 'order-2 flex basis-full h-10 items-center sm:order-1 sm:basis-0 sm:grow sm:h-auto'
                    }`}
                />

                <HeaderSlotHost expanded={!collapsed} />

                {/* The page's tabs (`PageTabs`). Tall: their own full-width line
                    under the title. Compact: on the title's line, taking what is
                    left and scrolling inside it. Under `lg` they keep a line of
                    their own, after the buttons, so the title never shares
                    358px with ten tabs. */}
                <div
                    id={HEADER_TABS_ID}
                    className={`min-w-0 empty:hidden basis-full ${
                        collapsed ? 'order-5 lg:order-3 lg:basis-0 lg:grow' : 'order-5 mt-2'
                    }`}
                />

                {/* The page's buttons: its own `actions`, or portalled in by the
                    page's `<PageHeader actions>`. Compact, a labelled button
                    shows its icon and keeps its label for the screen reader and
                    a hover tip (`.karr-compact-actions` in globals.css). */}
                <div
                    id={HEADER_ACTIONS_ID}
                    className={`order-4 ml-auto ${ACTIONS_ROW} empty:hidden ${collapsed ? 'karr-compact-actions !gap-1' : ''}`}
                >
                    {actions}
                </div>
            </div>
        </div>
    );
}
